import { useLayoutEffect, useRef, useState } from "react";
import { AlertTriangle, LogOut } from "lucide-react";

import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/cn";
import { useOptionalToast } from "@/components/ui/Toast";
import { ApiError } from "@/lib/api";

/**
 * Sticky amber banner pinned above the main content whenever a super admin
 * is impersonating a tenant account (`useAuth().viewAsAccountId !== null`).
 *
 * Mounted unconditionally by `AppShell`; renders nothing when there is no
 * view-as session. Renders the impersonated account name verbatim and an
 * "Exit" button that calls `exitViewAs()` (which itself hard-reloads the
 * same URL so every page re-fetches under cross-tenant scope).
 *
 * Accessibility:
 *   - `role="status"` + `aria-live="polite"` so screen readers announce the
 *     impersonation state when the banner first appears (e.g. immediately
 *     after a switch-account roundtrip), without interrupting whatever the
 *     user is currently reading.
 *   - The Exit button is a real `<button>`; focus order keeps it tabbable
 *     right after the AppShell header.
 */
export function ViewAsBanner() {
  const { viewAsAccountId, viewAsAccountName, exitViewAs } = useAuth();
  const { toast } = useOptionalToast();
  const [busy, setBusy] = useState(false);
  const bannerRef = useRef<HTMLDivElement>(null);

  // Publish the banner's rendered height to `--viewas-h` so AppShell's sticky
  // mobile header can offset beneath it (the two both want top-0). Clears the
  // var whenever the banner isn't shown so the header falls back to top-0.
  useLayoutEffect(() => {
    const root = document.documentElement;
    const el = bannerRef.current;
    if (!el) {
      root.style.removeProperty("--viewas-h");
      return;
    }
    const publish = () =>
      root.style.setProperty("--viewas-h", `${el.offsetHeight}px`);
    publish();
    // Height differs between touch (44px Exit target) and desktop, and long
    // account names can wrap — re-measure on resize where supported.
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(publish);
      ro.observe(el);
    }
    return () => {
      ro?.disconnect();
      root.style.removeProperty("--viewas-h");
    };
  }, [viewAsAccountId, viewAsAccountName]);

  if (!viewAsAccountId) return null;

  const handleExit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await exitViewAs();
      // exitViewAs hard-reloads on success; reaching the next line is the
      // network-error path. Clear busy so the button re-arms.
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.body.message ?? "Could not exit view-as mode."
          : "Could not exit view-as mode.";
      toast({ title: message, variant: "error" });
      setBusy(false);
    }
  };

  // The account name is rendered verbatim — the server resolves it from the
  // joined Account row, so falsy means the account was deleted out from
  // under the impersonation session. Fall back to the id so the banner is
  // never empty (which would be a confusing "viewing as nothing" state).
  const displayName = viewAsAccountName ?? viewAsAccountId;

  return (
    <div
      ref={bannerRef}
      role="status"
      aria-live="polite"
      data-testid="view-as-banner"
      className={cn(
        "sticky top-0 z-40 flex items-center justify-between gap-3",
        "border-b border-[var(--warn-line)] bg-[var(--warn-bg)] px-4 py-2",
        "text-[13px] text-text",
      )}
    >
      <div className="flex min-w-0 items-center gap-2">
        <AlertTriangle
          className="h-4 w-4 shrink-0 text-warn"
          strokeWidth={1.75}
          aria-hidden
        />
        <span className="truncate">
          Viewing as{" "}
          <strong className="font-semibold">{displayName}</strong> as super
          admin
        </span>
      </div>
      <button
        type="button"
        onClick={handleExit}
        disabled={busy}
        data-testid="view-as-banner-exit"
        className={cn(
          // Full-height touch target (≥44px) on mobile — this is the only way
          // out of impersonation and must not be a 26px sliver. Collapses back
          // to the compact strip look at ≥768px.
          "inline-flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-md border border-[var(--warn-line)] bg-surface px-4 py-2 text-xs font-medium",
          "md:min-h-0 md:px-3 md:py-1",
          "text-text transition-colors duration-fast ease hover:bg-[var(--warn-bg)]",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-warn",
          "disabled:opacity-60",
        )}
      >
        <LogOut className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
        Exit
      </button>
    </div>
  );
}
